import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const X_ORIGIN = 'https://platform.twitter.com';

// Keep third-party code on its own origin, never in a same-origin srcDoc.
// Only messages from this exact frame may confirm rendering or change its size.
export default function FeedXMedia({ id }) {
  const frame = useRef(null);
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);
  const [height, setHeight] = useState(560);
  const theme = useTheme().palette.mode;
  useEffect(() => {
    setStatus('loading');
    setHeight(560);
    const timer = setTimeout(() => setStatus('failed'), 15000);
    const receive = (event) => {
      if (event.origin !== X_ORIGIN || event.source !== frame.current?.contentWindow) return;
      const message = event.data?.['twttr.embed'];
      if (message?.params?.[0]?.data?.tweet_id !== id) return;
      if (message.method === 'twttr.private.rendered') {
        clearTimeout(timer);
        setStatus('ready');
      } else if (message.method === 'twttr.private.no_results') {
        clearTimeout(timer);
        setStatus('failed');
      } else if (message.method === 'twttr.private.resize') {
        const value = message.params[0].height;
        if (typeof value === 'number' && Number.isFinite(value)) {
          setHeight(Math.min(2400, Math.max(180, value)));
        }
      }
    };
    window.addEventListener('message', receive);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('message', receive);
    };
  }, [id, attempt, theme]);

  if (status === 'failed') {
    return (
      <Box role="status" sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 3 }}>
        <Typography>
          미디어를 불러오지 못했어요. 다시 시도하거나 아래 원문을 열어 주세요.
        </Typography>
        <Button
          onClick={() => {
            setStatus('loading');
            setAttempt((value) => value + 1);
          }}
        >
          다시 시도
        </Button>
      </Box>
    );
  }
  return (
    <Box>
      {status === 'loading' && <Typography role="status">X 게시물을 불러오는 중이에요.</Typography>}
      <Box
        key={`${id}-${attempt}-${theme}`}
        ref={frame}
        component="iframe"
        title="X 미디어"
        src={`${X_ORIGIN}/embed/Tweet.html?id=${id}&dnt=true&theme=${theme}&lang=ko`}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        allow="encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        onError={() => setStatus('failed')}
        sx={{ width: '100%', height, border: 0, display: 'block' }}
      />
    </Box>
  );
}
